/**
 * The unified map's forecast catalog (#327): configs grouped into models,
 * each model holding one config per rail variable, plus the Auto rule that
 * picks a model for the view and the ranked model list.
 *
 * A model is a (name, mesh) pair, not a name: ARPEGE 0P10 (Europe) and
 * ARPEGE 0P25 (Global) are different models with different domains.
 */
import type {
  ForecastMapConfig,
  ForecastMapModelDomain,
  ForecastMapModelSummary,
} from '../../api/types';
import {SupportedForecastModelList} from '../Forecast/constants';
import {
  interactivePredicate,
  isFullDomain,
} from '../ForecastMap/interactiveVariables';
import {type CoverageClass, classifyCoverage, viewCoverage} from './coverage';
import type {MapBounds} from './mapModes';

export type ForecastVariableKey =
  | 'temperature'
  | 'precipitation'
  | 'wind'
  | 'cloudCover';

/** The rail's variables, in rail order. */
export const FORECAST_VARIABLES: readonly ForecastVariableKey[] = [
  'temperature',
  'precipitation',
  'wind',
  'cloudCover',
];

/** A rail variable's key, e.g. from a URL or storage (#327 MR5d). */
export const isForecastVariableKey = (
  value: unknown,
): value is ForecastVariableKey =>
  typeof value === 'string' &&
  (FORECAST_VARIABLES as readonly string[]).includes(value);

type Layer = ForecastMapConfig['parameters']['layers'][number];

// Layers that carry colour, and so say what the map shows; isolines, barbs
// and arrows are line art drawn over them.
const COLOUR_LAYER_TYPES = new Set(['filled_contour', 'raster', 'magnitude']);
// u/v layers that draw the wind. (`fronts` also carries a u/v pair.)
const WIND_LAYER_TYPES = new Set(['magnitude', 'barbs', 'vector_field']);

/*
The config names its variable with the model's raw metric code (GRIB-style
shortnames: `2t`, `RR_1H`, `tcc`…), matched like `forecastMapPointMeasure`
does. First match wins; gusts come first so that `10fg` is not read as wind,
and map to no rail variable.
*/
const METRIC_VARIABLES: [RegExp, ForecastVariableKey | null][] = [
  [/gust|fg/, null],
  [/^(2t|t2m|2mt|t|tmp|skt)$|temp/, 'temperature'],
  [/^tp$|^rr|apcp|precip|rain/, 'precipitation'],
  [/^(10si|si10|ws|ff)$|wind|speed/, 'wind'],
  [/^(tcc|clct|tcdc)$|cloud/, 'cloudCover'],
];

const layerVariable = (layer: Layer): ForecastVariableKey | null => {
  if (!('metric' in layer)) {
    return WIND_LAYER_TYPES.has(layer.layer_type) ? 'wind' : null;
  }
  const metric = layer.metric.toLowerCase();
  const match = METRIC_VARIABLES.find(([pattern]) => pattern.test(metric));
  return match ? match[1] : null;
};

/**
 * The rail variable a config shows, read from its first colour layer (else
 * its first layer), or null when it shows none of them (pressure, gusts, air
 * quality, the synoptic maps).
 */
export const forecastVariableKey = (
  config: Pick<ForecastMapConfig, 'parameters'>,
): ForecastVariableKey | null => {
  const {layers} = config.parameters;
  const layer =
    layers.find((candidate) => COLOUR_LAYER_TYPES.has(candidate.layer_type)) ??
    layers[0];
  return layer === undefined ? null : layerVariable(layer);
};

/** `${name}|${mesh_size}`, e.g. `ARPEGE|0P10`. */
export type ForecastModelKey = string;

export const forecastModelKey = (
  model: Pick<ForecastMapModelSummary, 'name' | 'mesh_size'>,
): ForecastModelKey => `${model.name}|${model.mesh_size}`;

/**
 * The model's display name: its `SupportedForecastModelList` title, matched
 * on name and mesh (an entry with a null mesh matches any mesh), else the
 * server name with underscores as spaces. Model titles are proper nouns, the
 * same in every language; an entry whose title is a translated message falls
 * back to the server name too.
 */
export const forecastModelTitle = (
  model: Pick<ForecastMapModelSummary, 'name' | 'mesh_size'>,
): string => {
  const byName = SupportedForecastModelList.filter(
    (entry) => entry.serverId.forecastModel === model.name,
  );
  const entry =
    byName.find(
      (candidate) => candidate.serverId.meshSize === model.mesh_size,
    ) ?? byName.find((candidate) => candidate.serverId.meshSize === null);
  return typeof entry?.title === 'string'
    ? entry.title
    : model.name.replace(/_/g, ' ');
};

export type ForecastCatalogModel = {
  key: ForecastModelKey;
  title: string;
  /** Native grid spacing in km; null when unknown. */
  gridKm: number | null;
  /** null = global; undefined = unknown (an older payload). */
  domain: ForecastMapModelDomain | null | undefined;
  /** Every config of this model is locked (outside the user's tier). */
  locked: boolean;
  configs: Partial<Record<ForecastVariableKey, ForecastMapConfig>>;
};

/**
 * The catalog's models, in catalog order: full-domain interactive configs
 * only — owned (builder) configs, configs cropped to a zone and configs the
 * map cannot draw are left out, as are configs showing no rail variable (and
 * so a model left with none). Where a model has two configs for one
 * variable ("Wind" and "Wind speed"), the first in the catalog is kept --
 * the first this account may open, when a locked one comes before it.
 */
export const forecastCatalogModels = (
  configs: readonly ForecastMapConfig[],
): ForecastCatalogModel[] => {
  const isInteractive = interactivePredicate(configs);
  const models = new Map<ForecastModelKey, ForecastCatalogModel>();
  for (const config of configs) {
    const variable = forecastVariableKey(config);
    if (
      config.is_owned === true ||
      !isFullDomain(config) ||
      !isInteractive(config) ||
      variable === null
    ) {
      continue;
    }
    const summary = config.forecast_model;
    const key = forecastModelKey(summary);
    let model = models.get(key);
    if (model === undefined) {
      model = {
        key,
        title: forecastModelTitle(summary),
        gridKm: summary.grid_km ?? null,
        domain: summary.domain,
        locked: true,
        configs: {},
      };
      models.set(key, model);
    }
    // The first config per variable, except that a config this account may
    // open replaces a locked one listed before it (#327 MR7a).
    const kept = model.configs[variable];
    if (
      kept === undefined ||
      (kept.locked === true && config.locked !== true)
    ) {
      model.configs[variable] = config;
    }
    model.locked &&= config.locked === true;
  }
  return [...models.values()];
};

/** Auto only picks a model covering at least this much of the view. */
export const AUTO_COVERAGE_THRESHOLD = 0.9;
/**
 * Auto keeps its current model while it covers at least this much; below
 * it, a hand-picked model counts as having left the view (#327 MR5d).
 */
export const AUTO_KEEP_THRESHOLD = 0.85;

type Scored = {model: ForecastCatalogModel; coverage: number};

// An unknown domain (an older payload) is never treated as covering the
// view: the catalog has no other outline to measure.
const modelCoverage = (
  model: ForecastCatalogModel,
  view: Readonly<MapBounds>,
): number =>
  model.domain === undefined ? 0 : viewCoverage(view, model.domain);

const gridOf = (model: ForecastCatalogModel): number =>
  model.gridKm ?? Number.POSITIVE_INFINITY;

// Finer grid first, unknown grid last. Two unknowns: ∞ - ∞ is NaN, which the
// `||` chains of the callers read as a tie.
const byGrid = (a: ForecastCatalogModel, b: ForecastCatalogModel): number =>
  gridOf(a) - gridOf(b);

// Code-point order, not locale order: the same in every runtime.
const byKey = (a: ForecastCatalogModel, b: ForecastCatalogModel): number =>
  Number(a.key > b.key) - Number(a.key < b.key);

const isFiner = (a: ForecastCatalogModel, b: ForecastCatalogModel): boolean =>
  byGrid(a, b) < 0;

/**
 * The model Auto draws for `variable` over `view`, with its coverage.
 *
 * Candidates are models with an unlocked config for the variable (any
 * config with `includeLocked`: the model Auto would draw with Premium,
 * #327 MR7a). Among
 * those covering at least 90% of the view, the finest grid wins (unknown
 * grid last), then the higher coverage, then the key. With none at 90%, the
 * highest coverage wins, then the finest. Nothing overlapping: null.
 *
 * Hysteresis: the `current` model is kept while it covers at least
 * `keepThreshold` (85%) of the view, unless a strictly finer model now
 * reaches 90% — so a pan near the threshold does not flip models.
 */
export const pickAutoModel = ({
  models,
  view,
  variable,
  current = null,
  keepThreshold = AUTO_KEEP_THRESHOLD,
  includeLocked = false,
}: {
  models: readonly ForecastCatalogModel[];
  view: Readonly<MapBounds>;
  variable: ForecastVariableKey;
  current?: ForecastModelKey | null;
  keepThreshold?: number;
  includeLocked?: boolean;
}): Scored | null => {
  const overlapping: Scored[] = models
    .filter((model) => {
      const config = model.configs[variable];
      return config !== undefined && (includeLocked || config.locked !== true);
    })
    .map((model) => ({model, coverage: modelCoverage(model, view)}))
    .filter(({coverage}) => coverage > 0);
  const usable = overlapping
    .filter(({coverage}) => coverage >= AUTO_COVERAGE_THRESHOLD)
    .sort(
      (a, b) =>
        byGrid(a.model, b.model) ||
        b.coverage - a.coverage ||
        byKey(a.model, b.model),
    );
  const best =
    usable[0] ??
    [...overlapping].sort(
      (a, b) =>
        b.coverage - a.coverage ||
        byGrid(a.model, b.model) ||
        byKey(a.model, b.model),
    )[0] ??
    null;

  const kept = overlapping.find(
    ({model, coverage}) => model.key === current && coverage >= keepThreshold,
  );
  if (
    kept !== undefined &&
    !(usable[0] && isFiner(usable[0].model, kept.model))
  ) {
    return kept;
  }
  return best;
};

/**
 * The Premium model worth naming to a free account (#327 MR7a, target T13):
 * the model Auto would draw for `variable` over `view` if nothing were
 * locked -- when that model is locked, covers at least 90% of the view and
 * has a finer grid than `autoModel` (the one Auto draws for this account;
 * null: none). Else null: a Premium catalog (nothing locked), a view where
 * the free model is as fine as anything, a locked model covering only part
 * of the view ("covers this view" must be true).
 */
export const premiumUpsellModel = ({
  models,
  view,
  variable,
  autoModel,
}: {
  models: readonly ForecastCatalogModel[];
  view: Readonly<MapBounds>;
  variable: ForecastVariableKey;
  autoModel: ForecastCatalogModel | null;
}): ForecastCatalogModel | null => {
  const best = pickAutoModel({models, view, variable, includeLocked: true});
  if (
    best === null ||
    best.model.configs[variable]?.locked !== true ||
    best.coverage < AUTO_COVERAGE_THRESHOLD ||
    (autoModel !== null && !isFiner(best.model, autoModel))
  ) {
    return null;
  }
  return best.model;
};

/**
 * The rail's variables, in rail order (#327 MR7a): `usable` -- some model
 * has an unlocked config for it -- and `locked` -- only locked configs have
 * it (cloud cover, ECMWF HRES's, for a free account). A variable no model
 * has is in neither.
 */
export const forecastRailVariables = (
  models: readonly ForecastCatalogModel[],
): {usable: ForecastVariableKey[]; locked: ForecastVariableKey[]} => {
  const usable: ForecastVariableKey[] = [];
  const locked: ForecastVariableKey[] = [];
  for (const variable of FORECAST_VARIABLES) {
    const configs = models.flatMap((model) => {
      const config = model.configs[variable];
      return config === undefined ? [] : [config];
    });
    if (configs.some((config) => config.locked !== true)) {
      usable.push(variable);
    } else if (configs.length > 0) {
      locked.push(variable);
    }
  }
  return {usable, locked};
};

export type ForecastModelListEntry = {
  model: ForecastCatalogModel;
  coverage: CoverageClass;
  /** Why the model is under "Other areas"; null "In this area". */
  reason: 'outside' | 'unknown' | 'noVariable' | null;
  /**
   * Its config for the variable is locked: picking it offers Premium
   * (#327 MR7a). Never for a model without the variable.
   */
  locked: boolean;
};

// "In this area" rows are full or partial.
const fractionOf = (coverage: CoverageClass): number =>
  coverage.kind === 'partial' ? coverage.fraction : 1;

/**
 * The model list for `variable` over `view`.
 *
 * "In this area": full coverage before partial; full rows by grid (unknown
 * last), partial rows by coverage, then grid; the key breaks ties.
 * "Other areas", by title: models with no config for the variable
 * (`'noVariable'`, whatever their coverage), else of unknown domain
 * (`'unknown'`, an older payload), else covering none of the view
 * (`'outside'`).
 */
export const forecastModelList = ({
  models,
  view,
  variable,
}: {
  models: readonly ForecastCatalogModel[];
  view: Readonly<MapBounds>;
  variable: ForecastVariableKey;
}): {
  inArea: ForecastModelListEntry[];
  otherAreas: ForecastModelListEntry[];
} => {
  const inArea: ForecastModelListEntry[] = [];
  const otherAreas: ForecastModelListEntry[] = [];
  for (const model of models) {
    const coverage = classifyCoverage(modelCoverage(model, view));
    const locked = model.configs[variable]?.locked === true;
    if (model.configs[variable] === undefined) {
      otherAreas.push({model, coverage, reason: 'noVariable', locked: false});
    } else if (model.domain === undefined) {
      otherAreas.push({model, coverage, reason: 'unknown', locked});
    } else if (coverage.kind === 'none') {
      otherAreas.push({model, coverage, reason: 'outside', locked});
    } else {
      inArea.push({model, coverage, reason: null, locked});
    }
  }
  inArea.sort(
    (a, b) =>
      Number(b.coverage.kind === 'full') - Number(a.coverage.kind === 'full') ||
      fractionOf(b.coverage) - fractionOf(a.coverage) ||
      byGrid(a.model, b.model) ||
      byKey(a.model, b.model),
  );
  otherAreas.sort(
    (a, b) =>
      a.model.title.localeCompare(b.model.title) || byKey(a.model, b.model),
  );
  return {inArea, otherAreas};
};
